// ============================================================================
// MY BOOKINGS — sortable, filterable, paginated table of studio bookings
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarDays, CalendarPlus, Loader, AlertCircle, CheckCircle, X,
  ChevronDown, ChevronUp, ArrowUp, ArrowDown, ChevronsUpDown, Search,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  listMyBookings,
  cancelBooking,
  StudioBooking,
  BookingStatus,
  DATE_TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  TIER_LABELS
} from '../services/studio-bookings';

// ============================================================================
// TYPES
// ============================================================================

type SortCol = 'status' | 'customer' | 'room' | 'date' | 'eventType' | 'facilitator';
type SortDir = 'asc' | 'desc';

interface SortState { col: SortCol; dir: SortDir }

interface Filters {
  status: string;
  customer: string;
  room: string;
  eventType: string;
  facilitator: string;
}

const PAGE_SIZES = [10, 25, 50];

// ============================================================================
// HELPERS
// ============================================================================

const STATUS_DOT: Record<BookingStatus, string> = {
  pending:   'bg-amber-400',
  approved:  'bg-green-500',
  rejected:  'bg-red-500',
  cancelled: 'bg-slate-400'
};

const STATUS_ORDER: Record<BookingStatus, number> = {
  pending: 0, approved: 1, rejected: 2, cancelled: 3
};

function applyFilters(bookings: StudioBooking[], f: Filters): StudioBooking[] {
  return bookings.filter(b => {
    if (f.status && b.status !== f.status) return false;
    if (f.customer && !b.customer.toLowerCase().includes(f.customer.toLowerCase())) return false;
    if (f.room && b.roomName !== f.room) return false;
    if (f.eventType && b.eventTypeName !== f.eventType) return false;
    if (f.facilitator && !b.facilitatorName.toLowerCase().includes(f.facilitator.toLowerCase())) return false;
    return true;
  });
}

function applySort(bookings: StudioBooking[], sort: SortState | null): StudioBooking[] {
  if (!sort) return bookings;
  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...bookings].sort((a, b) => {
    switch (sort.col) {
      case 'status':      return mult * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
      case 'customer':    return mult * a.customer.localeCompare(b.customer);
      case 'room':        return mult * a.roomName.localeCompare(b.roomName);
      case 'date':        return mult * a.startDate.localeCompare(b.startDate);
      case 'eventType':   return mult * a.eventTypeName.localeCompare(b.eventTypeName);
      case 'facilitator': return mult * a.facilitatorName.localeCompare(b.facilitatorName);
      default: return 0;
    }
  });
}

function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StatusBadge({ status }: { status: BookingStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

function SortIcon({ col, sort }: { col: SortCol; sort: SortState | null }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
  return sort.dir === 'asc'
    ? <ArrowUp className="w-3.5 h-3.5 text-primary" />
    : <ArrowDown className="w-3.5 h-3.5 text-primary" />;
}

function ColHeader({
  col, label, sort, onSort
}: { col: SortCol; label: string; sort: SortState | null; onSort: (col: SortCol) => void }) {
  return (
    <th
      className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 cursor-pointer select-none hover:text-slate-600 transition-colors whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      <div className="flex items-center gap-1.5">
        {label}
        <SortIcon col={col} sort={sort} />
      </div>
    </th>
  );
}

function BookingRow({ booking, onCancel }: { booking: StudioBooking; onCancel: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const canCancel = ['pending', 'approved'].includes(booking.status);
  const isPast = booking.endDate < new Date().toISOString().split('T')[0];
  const hasNotes = !!booking.reviewNotes;

  async function handleCancel() {
    setCancelling(true);
    try {
      await cancelBooking(booking.bookingId);
      onCancel();
    } finally {
      setCancelling(false);
      setShowConfirm(false);
    }
  }

  const dateLabel = booking.startDate === booking.endDate
    ? formatDate(booking.startDate)
    : `${formatDate(booking.startDate)} → ${formatDate(booking.endDate)}`;

  return (
    <>
      <tr className="border-b border-[#e2e8f0] hover:bg-slate-50/60 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex flex-col gap-1">
            <StatusBadge status={booking.status} />
            {isPast && booking.status !== 'cancelled' && (
              <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full w-fit">Past</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className="font-semibold text-navy text-sm">{booking.customer}</span>
        </td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{booking.roomName}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
          <div>{dateLabel}</div>
          <div className="text-xs text-slate-400">{DATE_TYPE_LABELS[booking.dateType]}</div>
        </td>
        <td className="px-4 py-3 text-sm text-slate-500">{booking.eventTypeName}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
          <div>{booking.facilitatorName}</div>
          <div className="text-xs text-slate-400">{TIER_LABELS[booking.facilitatorTier]}</div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2 justify-end">
            {hasNotes && (
              <button
                onClick={() => setExpanded(e => !e)}
                className="text-slate-400 hover:text-navy p-1 rounded-lg hover:bg-slate-100 transition-colors"
                title="View notes"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
            {canCancel && !isPast && !showConfirm && (
              <button
                onClick={() => setShowConfirm(true)}
                className="flex items-center gap-1 h-7 px-2.5 rounded-lg border border-red-200 text-red-500 text-xs font-medium hover:bg-red-50 hover:border-red-300 transition-colors"
              >
                <X className="w-3 h-3" /> Cancel
              </button>
            )}
            {showConfirm && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="h-7 px-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold disabled:opacity-60 transition-colors"
                >
                  {cancelling ? 'Cancelling…' : 'Confirm'}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="h-7 w-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-100 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
      {expanded && hasNotes && (
        <tr className="border-b border-[#e2e8f0] bg-slate-50">
          <td colSpan={7} className="px-6 pb-3 pt-0">
            <div className="p-3 rounded-xl bg-white border border-[#e2e8f0]">
              <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                {booking.status === 'rejected' ? 'Rejection reason' : 'Review notes'}
              </p>
              <p className="text-sm text-navy">{booking.reviewNotes}</p>
              {booking.reviewedByName && (
                <p className="text-xs text-slate-400 mt-1">— {booking.reviewedByName}</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function MyBookings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState<StudioBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>((location.state as any)?.flash || null);

  const [sort, setSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<Filters>({ status: '', customer: '', room: '', eventType: '', facilitator: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (flash) {
      const t = setTimeout(() => setFlash(null), 5000);
      return () => clearTimeout(t);
    }
  }, [flash]);

  function load() {
    setLoading(true);
    listMyBookings()
      .then(setBookings)
      .catch(() => setError('Failed to load bookings'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  const rooms = useMemo(() => [...new Set(bookings.map(b => b.roomName))].sort(), [bookings]);
  const eventTypes = useMemo(() => [...new Set(bookings.map(b => b.eventTypeName))].sort(), [bookings]);

  const filtered   = useMemo(() => applyFilters(bookings, filters), [bookings, filters]);
  const sorted     = useMemo(() => applySort(filtered, sort), [filtered, sort]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  function handleSort(col: SortCol) {
    setSort(prev => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
    setPage(1);
  }

  function setFilter(key: keyof Filters, val: string) {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const inputCls = "h-8 rounded-lg border border-[#e2e8f0] bg-white px-2.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <CalendarDays size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">My Bookings</h1>
              <p className="text-slate-500 text-sm font-medium">Your Co-Creation Studio booking requests</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/studio/book')}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <CalendarPlus size={16} /> Book a session
          </button>
        </header>

        {/* Flash */}
        {flash && (
          <div className="p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700 font-medium">{flash}</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="rounded-xl bg-white p-12 border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <CalendarDays size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No bookings yet.</p>
            <p className="text-slate-400 text-xs mt-2">Book a Co-Creation Studio room to get started.</p>
            <button
              onClick={() => navigate('/studio/book')}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
            >
              <CalendarPlus size={16} /> Book your first session
            </button>
          </div>
        ) : (
          <>
            {/* Filter + Table */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              {/* Filter bar */}
              <div className="p-4 border-b border-[#e2e8f0]">
                <div className="flex flex-wrap gap-3 items-center">
                  <select
                    value={filters.status}
                    onChange={e => setFilter('status', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">All statuses</option>
                    {(['pending', 'approved', 'rejected', 'cancelled'] as BookingStatus[]).map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>

                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Customer"
                      value={filters.customer}
                      onChange={e => setFilter('customer', e.target.value)}
                      className={`${inputCls} pl-6 w-36`}
                    />
                  </div>

                  <select
                    value={filters.room}
                    onChange={e => setFilter('room', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">All rooms</option>
                    {rooms.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>

                  <select
                    value={filters.eventType}
                    onChange={e => setFilter('eventType', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">All event types</option>
                    {eventTypes.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>

                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Facilitator"
                      value={filters.facilitator}
                      onChange={e => setFilter('facilitator', e.target.value)}
                      className={`${inputCls} pl-6 w-36`}
                    />
                  </div>

                  {hasActiveFilters && (
                    <button
                      onClick={() => { setFilters({ status: '', customer: '', room: '', eventType: '', facilitator: '' }); setPage(1); }}
                      className="h-8 px-2.5 text-xs text-slate-500 hover:text-navy flex items-center gap-1 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <X className="w-3 h-3" /> Clear
                    </button>
                  )}

                  <span className="ml-auto text-xs text-slate-400">
                    {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
                  </span>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                      <ColHeader col="status"      label="Status"      sort={sort} onSort={handleSort} />
                      <ColHeader col="customer"    label="Customer"    sort={sort} onSort={handleSort} />
                      <ColHeader col="room"        label="Room"        sort={sort} onSort={handleSort} />
                      <ColHeader col="date"        label="Date"        sort={sort} onSort={handleSort} />
                      <ColHeader col="eventType"   label="Event type"  sort={sort} onSort={handleSort} />
                      <ColHeader col="facilitator" label="Facilitator" sort={sort} onSort={handleSort} />
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">
                          No bookings match the current filters.
                        </td>
                      </tr>
                    ) : (
                      paginated.map(b => <BookingRow key={b.bookingId} booking={b} onCancel={load} />)
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                Rows per page:
                {PAGE_SIZES.map(s => (
                  <button
                    key={s}
                    onClick={() => { setPageSize(s); setPage(1); }}
                    className={`px-2 py-1 rounded-lg transition-colors ${pageSize === s ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-slate-100'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 mr-2">
                  {sorted.length === 0 ? '0' : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, sorted.length)}`} of {sorted.length}
                </span>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-slate-600" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                  .reduce<(number | '…')[]>((acc, p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '…' ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-xs text-slate-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${safePage === p ? 'bg-primary text-white' : 'hover:bg-slate-100 text-slate-600'}`}
                    >
                      {p}
                    </button>
                  ))
                }
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
